
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { formatEventType, getSeverityColor } from "@/utils/securityUtils";
import { format } from "date-fns";

type SecurityEvent = {
  id: string;
  created_at: string;
  event_type: string;
  severity: string;
  ip_address: string | null;
  user_agent: string | null;
  event_details: any;
  resolved: boolean;
  resolution_notes: string | null;
  action_taken: string | null;
  user_id: string | null;
  user_email: string | null;
  timestamp: string;
  action_taken_at: string | null;
  action_taken_by: string | null;
};

export default function AdminSecurityEventDetail() {
  const { id } = useParams<{ id: string }>();

  const { data: event, isLoading } = useQuery({
    queryKey: ['security-event', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('security_events')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      return data as SecurityEvent;
    },
    enabled: !!id
  });

  if (isLoading) {
    return (
      <div className="container max-w-7xl mx-auto py-8 px-4">
        <div className="flex items-center justify-center">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container max-w-7xl mx-auto py-8 px-4">
        <div className="flex items-center justify-center">
          <p>Event not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-7xl mx-auto py-8 px-4 space-y-8">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/security">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold">Security Event Details</h1>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Event Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="font-semibold">Event Type</p>
                <p>{formatEventType(event.event_type)}</p>
              </div>
              <div>
                <p className="font-semibold">Severity</p>
                <p className={getSeverityColor(event.severity)}>{event.severity}</p>
              </div>
              <div>
                <p className="font-semibold">Created At</p>
                <p>{format(new Date(event.created_at), 'PPpp')}</p>
              </div>
              <div>
                <p className="font-semibold">Status</p>
                <p className={`inline-flex px-2 py-1 rounded-full text-xs ${
                  event.resolved 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {event.resolved ? 'Resolved' : 'Unresolved'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>User Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="font-semibold">User Email</p>
                <p>{event.user_email || 'N/A'}</p>
              </div>
              <div>
                <p className="font-semibold">User ID</p>
                <p>{event.user_id || 'N/A'}</p>
              </div>
              <div>
                <p className="font-semibold">IP Address</p>
                <p>{event.ip_address || 'N/A'}</p>
              </div>
              <div>
                <p className="font-semibold">User Agent</p>
                <p className="truncate">{event.user_agent || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {event.event_details && (
          <Card>
            <CardHeader>
              <CardTitle>Event Details</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-muted p-4 rounded-lg overflow-auto">
                {JSON.stringify(event.event_details, null, 2)}
              </pre>
            </CardContent>
          </Card>
        )}

        {event.resolved && (
          <Card>
            <CardHeader>
              <CardTitle>Resolution Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-semibold">Action Taken</p>
                  <p>{event.action_taken || 'N/A'}</p>
                </div>
                <div>
                  <p className="font-semibold">Action Taken At</p>
                  <p>{event.action_taken_at ? format(new Date(event.action_taken_at), 'PPpp') : 'N/A'}</p>
                </div>
                <div className="col-span-2">
                  <p className="font-semibold">Resolution Notes</p>
                  <p>{event.resolution_notes || 'No notes provided'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
